import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { 
  ArrowLeft, Folder, FolderPlus, Image, Upload, Search, Loader2, 
  Trash2, Move, Tag, ChevronRight, Home, X, Check,
  FolderOpen, ImagePlus, HardDrive, Grid, List, Settings, Plus, Edit2, Palette, Filter, Download,
  BookOpen, ExternalLink, Droplets, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import ExportModal from '../../components/ExportModal';
import { PictureManagerUploadDialog } from '../../components/common/PictureManagerUploadDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color options for categories and tags
const COLOR_OPTIONS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#84CC16'
];

// Page size options
const PAGE_SIZE_OPTIONS = [12, 24, 48];

const PictureManagerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFullAdmin = user?.role === 'admin';

  // Tab state
  const [activeTab, setActiveTab] = useState('library');

  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [folderTree, setFolderTree] = useState({ folders: [], uncategorized_count: 0 });
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [pageSize, setPageSize] = useState(24);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0, per_page: 24 });

  // Filter states
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTags, setFilterTags] = useState([]);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [tagSearchResults, setTagSearchResults] = useState([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagSearchRef = useRef(null);

  // Categories and Tags
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  // Dialogs
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [imagesToMove, setImagesToMove] = useState([]);
  const [targetFolderId, setTargetFolderId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [exportFilters, setExportFilters] = useState({
    folder_id: 'all',
    category_id: 'all'
  });

  // Category/Tag management
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', color: '#6366F1', description: '' });
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({ name: '', color: '' });

  // Folder rename
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [newFolderRename, setNewFolderRename] = useState('');

  // Image preview edit state (inline editing)
  const [previewCategory, setPreviewCategory] = useState('');
  const [previewTags, setPreviewTags] = useState([]);
  const [previewTagInput, setPreviewTagInput] = useState('');
  const [previewTagSuggestions, setPreviewTagSuggestions] = useState([]);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [previewCaption, setPreviewCaption] = useState('');
  const [previewDisplayName, setPreviewDisplayName] = useState('');
  const [displayNameWarning, setDisplayNameWarning] = useState('');

  // Fetch folder tree and stats
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [treeRes, statsRes, catsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/picture-manager/folders/tree`),
        axios.get(`${API}/picture-manager/stats`),
        axios.get(`${API}/picture-manager/categories`),
        axios.get(`${API}/picture-manager/tags`)
      ]);
      setFolderTree(treeRes.data);
      setStats(statsRes.data);
      setCategories(catsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load picture manager data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch images for current folder
  const fetchImages = useCallback(async (folderId, page = 1, search = '', categoryId = 'all', tagList = [], perPage = 24) => {
    try {
      setImagesLoading(true);
      const params = { page, per_page: perPage };
      if (folderId === 'uncategorized') {
        params.folder_id = 'uncategorized';
      } else if (folderId) {
        params.folder_id = folderId;
      }
      if (search) {
        params.search = search;
      }
      if (categoryId && categoryId !== 'all') {
        params.category_id = categoryId;
      }
      if (tagList.length > 0) {
        params.tags = tagList.join(',');
      }
      const response = await axios.get(`${API}/picture-manager/images`, { params });
      setImages(response.data.items);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages,
        per_page: perPage
      });
    } catch (error) {
      console.error('Failed to fetch images:', error);
      toast.error('Failed to load images');
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'library') {
      const folderId = currentFolder?.id || (currentFolder === 'uncategorized' ? 'uncategorized' : null);
      fetchImages(folderId, 1, searchQuery, filterCategory, filterTags, pageSize);
      setSelectedImages(new Set());
    }
  }, [currentFolder, fetchImages, searchQuery, filterCategory, filterTags, pageSize, activeTab]);

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagSearchRef.current && !tagSearchRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search tags for filter
  useEffect(() => {
    if (tagSearchQuery.trim()) {
      const filtered = tags.filter(t => 
        t.name.toLowerCase().includes(tagSearchQuery.toLowerCase()) &&
        !filterTags.includes(t.name)
      );
      setTagSearchResults(filtered);
    } else {
      setTagSearchResults(tags.filter(t => !filterTags.includes(t.name)).slice(0, 10));
    }
  }, [tagSearchQuery, tags, filterTags]);

  // Navigate to folder
  const navigateToFolder = (folder) => {
    if (folder === null) {
      setCurrentFolder(null);
      setBreadcrumb([]);
    } else if (folder === 'uncategorized') {
      setCurrentFolder('uncategorized');
      setBreadcrumb([{ id: 'uncategorized', name: 'Uncategorized' }]);
    } else {
      setCurrentFolder(folder);
      const pathParts = folder.path.split('/');
      const newBreadcrumb = pathParts.map((name, index) => {
        const partialPath = pathParts.slice(0, index + 1).join('/');
        const matchingFolder = findFolderByPath(folderTree.folders, partialPath);
        return { id: matchingFolder?.id || folder.id, name };
      });
      setBreadcrumb(newBreadcrumb);
    }
  };

  const findFolderByPath = (folders, path) => {
    for (const folder of folders) {
      if (folder.path === path) return folder;
      if (folder.children?.length) {
        const found = findFolderByPath(folder.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/picture-manager/folders`, {
        name: newFolderName.trim(),
        parent_id: currentFolder?.id || null
      });
      toast.success('Folder created successfully');
      setCreateFolderOpen(false);
      setNewFolderName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  // Rename folder
  const openRenameFolder = (folder) => {
    setFolderToRename(folder);
    setNewFolderRename(folder.name);
    setRenameFolderOpen(true);
  };

  const handleRenameFolder = async () => {
    if (!newFolderRename.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    try {
      setSaving(true);
      await axios.put(`${API}/picture-manager/folders/${folderToRename.id}`, {
        name: newFolderRename.trim()
      });
      toast.success('Folder renamed');
      setRenameFolderOpen(false);
      setFolderToRename(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename folder');
    } finally {
      setSaving(false);
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folder) => {
    try {
      setSaving(true);
      await axios.delete(`${API}/picture-manager/folders/${folder.id}`);
      toast.success('Folder deleted');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      if (currentFolder?.id === folder.id) {
        navigateToFolder(null);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setSaving(false);
    }
  };

  // Delete image
  const handleDeleteImage = async (image) => {
    try {
      setSaving(true);
      await axios.delete(`${API}/picture-manager/images/${image.id}`);
      toast.success('Image deleted');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setImagePreviewOpen(false);
      fetchImages(currentFolder?.id || null, pagination.page, searchQuery, filterCategory, filterTags, pageSize);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    } finally {
      setSaving(false);
    }
  };

  // Move images
  const handleMoveImages = async () => {
    if (imagesToMove.length === 0) return;
    
    try {
      setSaving(true);
      await axios.post(`${API}/picture-manager/images/bulk-move`, {
        image_ids: imagesToMove,
        target_folder_id: targetFolderId
      });
      toast.success(`Moved ${imagesToMove.length} image${imagesToMove.length > 1 ? 's' : ''}`);
      setMoveDialogOpen(false);
      setImagesToMove([]);
      setTargetFolderId(null);
      setSelectedImages(new Set());
      fetchImages(currentFolder?.id || null, pagination.page, searchQuery, filterCategory, filterTags, pageSize);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move images');
    } finally {
      setSaving(false);
    }
  };

  const toggleImageSelection = (imageId) => {    const newSelection = new Set(selectedImages);
    if (newSelection.has(imageId)) {
      newSelection.delete(imageId);
    } else {
      newSelection.add(imageId);
    }
    setSelectedImages(newSelection);
  };

  // Open move dialog
  const openMoveDialog = (imageIds) => {
    setImagesToMove(imageIds);
    setTargetFolderId(null);
    setMoveDialogOpen(true);
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // ==================== IMAGE PREVIEW WITH INLINE EDITING ====================

  const openImagePreview = (image) => {
    setSelectedImage(image);
    setPreviewCategory(image.category_id || '');
    setPreviewTags(image.tags || []);
    setPreviewTagInput('');
    setPreviewTagSuggestions([]);
    setPreviewCaption(image.caption || '');
    setPreviewDisplayName(image.original_filename || '');
    setDisplayNameWarning('');
    setImagePreviewOpen(true);
  };

  const fetchPreviewTagSuggestions = async (query) => {
    if (!query.trim()) {
      setPreviewTagSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/picture-manager/tags/suggestions`, {
        params: { query }
      });
      setPreviewTagSuggestions(response.data.filter(s => !previewTags.includes(s.name)));
    } catch (error) {
      console.error('Failed to fetch tag suggestions:', error);
    }
  };

  const addPreviewTag = (tagName) => {
    const normalized = tagName.trim().toLowerCase();
    if (normalized && !previewTags.includes(normalized)) {
      setPreviewTags([...previewTags, normalized]);
    }
    setPreviewTagInput('');
    setPreviewTagSuggestions([]);
  };

  const removePreviewTag = (tagName) => {
    setPreviewTags(previewTags.filter(t => t !== tagName));
  };

  const saveImageChanges = async () => {
    try {
      setPreviewSaving(true);
      const trimmedDisplayName = previewDisplayName.trim();
      await axios.put(`${API}/picture-manager/images/${selectedImage.id}`, {
        category_id: previewCategory || null,
        tags: previewTags,
        caption: previewCaption,
        original_filename: trimmedDisplayName || undefined
      });
      toast.success('Image updated');
      // Update local state
      const updatedImage = {
        ...selectedImage,
        category_id: previewCategory || null,
        tags: previewTags,
        caption: previewCaption,
        original_filename: trimmedDisplayName || selectedImage.original_filename,
        category: previewCategory ? categories.find(c => c.id === previewCategory) : null
      };
      setSelectedImage(updatedImage);
      setDisplayNameWarning('');
      fetchImages(currentFolder?.id || null, pagination.page, searchQuery, filterCategory, filterTags, pageSize);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update image');
    } finally {
      setPreviewSaving(false);
    }
  };

  // Check if preview has unsaved changes
  const hasUnsavedChanges = () => {
    if (!selectedImage) return false;
    const originalCategory = selectedImage.category_id || '';
    const originalTags = selectedImage.tags || [];
    const originalCaption = selectedImage.caption || '';
    const originalDisplayName = selectedImage.original_filename || '';
    return previewCategory !== originalCategory ||
           JSON.stringify(previewTags.sort()) !== JSON.stringify([...originalTags].sort()) ||
           previewCaption !== originalCaption ||
           previewDisplayName !== originalDisplayName;
  };

  // ==================== CATEGORY MANAGEMENT ====================
  
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, color: category.color, description: category.description || '' });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', color: '#6366F1', description: '' });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    try {
      setSaving(true);
      if (editingCategory) {
        await axios.put(`${API}/picture-manager/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/picture-manager/categories`, categoryForm);
        toast.success('Category created');
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? Images will become uncategorized.`)) return;
    try {
      await axios.delete(`${API}/picture-manager/categories/${category.id}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // ==================== TAG MANAGEMENT ====================

  const openTagDialog = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setTagForm({ name: tag.name, color: tag.color || '' });
    } else {
      setEditingTag(null);
      setTagForm({ name: '', color: '' });
    }
    setTagDialogOpen(true);
  };

  const handleSaveTag = async () => {
    if (!tagForm.name.trim()) {
      toast.error('Please enter a tag name');
      return;
    }
    try {
      setSaving(true);
      if (editingTag) {
        await axios.put(`${API}/picture-manager/tags/${editingTag.id}`, tagForm);
        toast.success('Tag updated');
      } else {
        await axios.post(`${API}/picture-manager/tags`, tagForm);
        toast.success('Tag created');
      }
      setTagDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? It will be removed from all images.`)) return;
    try {
      await axios.delete(`${API}/picture-manager/tags/${tag.id}`);
      toast.success('Tag deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tag');
    }
  };

  // Export handlers
  const fetchExportCount = async () => {
    try {
      const params = new URLSearchParams();
      if (exportFilters.folder_id !== 'all') params.append('folder_id', exportFilters.folder_id);
      if (exportFilters.category_id !== 'all') params.append('category_id', exportFilters.category_id);
      
      const response = await axios.get(`${API}/export/pictures/count?${params}`);
      setExportCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch export count:', error);
    }
  };

  const handleExport = async (format, activeFilters, selectedColumns) => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      params.append('format', format);
      if (activeFilters.folder_id) params.append('folder_id', activeFilters.folder_id);
      if (activeFilters.category_id) params.append('category_id', activeFilters.category_id);
      if (selectedColumns.length > 0) params.append('columns', selectedColumns.join(','));
      
      const response = await axios.get(`${API}/export/pictures?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pictures_export.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Fetch export count when export modal opens or filters change
  useEffect(() => {
    if (showExportModal) {
      fetchExportCount();
    }
  }, [showExportModal, exportFilters]);

  // Toggle filter tag
  const toggleFilterTag = (tagName) => {
    if (filterTags.includes(tagName)) {
      setFilterTags(filterTags.filter(t => t !== tagName));
    } else {
      setFilterTags([...filterTags, tagName]);
    }
    setTagSearchQuery('');
    setShowTagDropdown(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterCategory('all');
    setFilterTags([]);
    setSearchQuery('');
  };

  // Render folder tree for move dialog
  const renderFolderOptions = (folders, depth = 0) => {
    return folders.map(folder => (
      <React.Fragment key={folder.id}>
        <div
          onClick={() => setTargetFolderId(folder.id)}
          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
            targetFolderId === folder.id ? 'bg-[#14B8A6]/10 border border-[#14B8A6]' : ''
          }`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          <Folder className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-sm">{folder.name}</span>
          <span className="text-xs text-gray-400">({folder.image_count})</span>
        </div>
        {folder.children?.length > 0 && renderFolderOptions(folder.children, depth + 1)}
      </React.Fragment>
    ));
  };

  // Calculate pagination display
  const getPaginationInfo = () => {
    const start = (pagination.page - 1) * pagination.per_page + 1;
    const end = Math.min(pagination.page * pagination.per_page, pagination.total);
    return { start, end, total: pagination.total };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#14B8A6]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition" data-testid="picture-manager-page">
        {/* Header */}
        <Card className="mb-6 border-l-4 border-l-[#14B8A6] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#14B8A6]/5 to-[#6366F1]/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/content')}
                  className="mr-2"
                  data-testid="back-to-content"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <div className="p-3 bg-[#14B8A6] rounded-lg">
                  <Image className="w-8 h-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-[#14B8A6]">Picture Manager</CardTitle>
                  <CardDescription>Organize and manage your image library</CardDescription>
                </div>
              </div>
              {/* Stats & Export Button */}
              <div className="flex items-center gap-4">
                {stats && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Image className="w-4 h-4" />
                      <span>{stats.total_images} images</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Folder className="w-4 h-4" />
                      <span>{stats.total_folders} folders</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <HardDrive className="w-4 h-4" />
                      <span>{stats.total_size_mb} MB</span>
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowExportModal(true)}
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="library" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Library
            </TabsTrigger>
            {isFullAdmin && (
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </TabsTrigger>
            )}
            <TabsTrigger value="best-practices" className="flex items-center gap-2" data-testid="tab-best-practices">
              <BookOpen className="w-4 h-4" />
              Best Practices
            </TabsTrigger>
          </TabsList>

          {/* Library Tab */}
          <TabsContent value="library">
            <div className="flex gap-6">
              {/* Sidebar - Folder Tree Only */}
              <div className="w-64 flex-shrink-0">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">Folders</CardTitle>
                      {isFullAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCreateFolderOpen(true)}
                          data-testid="create-folder-btn"
                        >
                          <FolderPlus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {/* All Images */}
                    <div
                      onClick={() => navigateToFolder(null)}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                        currentFolder === null ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : ''
                      }`}
                      data-testid="all-images-folder"
                    >
                      <Home className="w-4 h-4" />
                      <span className="text-sm font-medium">All Images</span>
                    </div>

                    {/* Uncategorized */}
                    <div
                      onClick={() => navigateToFolder('uncategorized')}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                        currentFolder === 'uncategorized' ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : ''
                      }`}
                      data-testid="uncategorized-folder"
                    >
                      <FolderOpen className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Uncategorized</span>
                      <span className="text-xs text-gray-400">({folderTree.uncategorized_count})</span>
                    </div>

                    {/* Folder Tree */}
                    <div className="mt-2 space-y-1">
                      {folderTree.folders.map(folder => (
                        <FolderTreeItem
                          key={folder.id}
                          folder={folder}
                          currentFolder={currentFolder}
                          onSelect={navigateToFolder}
                          onRename={isFullAdmin ? openRenameFolder : null}
                          onDelete={isFullAdmin ? (f) => { setItemToDelete({ type: 'folder', item: f }); setDeleteConfirmOpen(true); } : null}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Main Content */}
              <div className="flex-1">
                <Card>
                  <CardHeader className="pb-4">
                    {/* Breadcrumb */}
                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-4">
                      <span 
                        className="cursor-pointer hover:text-[#14B8A6]"
                        onClick={() => navigateToFolder(null)}
                      >
                        Root
                      </span>
                      {breadcrumb.map((crumb, index) => (
                        <React.Fragment key={crumb.id}>
                          <ChevronRight className="w-4 h-4" />
                          <span 
                            className={`cursor-pointer hover:text-[#14B8A6] ${
                              index === breadcrumb.length - 1 ? 'text-[#14B8A6] font-medium' : ''
                            }`}
                          >
                            {crumb.name}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Top Filter Bar */}
                    <div className="flex flex-col gap-3">
                      {/* Row 1: Search, Filters, Actions */}
                      <div className="flex items-center gap-3">
                        {/* Search */}
                        <div className="relative flex-1 max-w-xs">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            placeholder="Search images..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                            data-testid="search-images"
                          />
                        </div>

                        {/* Category Filter */}
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                          <SelectTrigger className="w-44">
                            <div className="flex items-center gap-2">
                              <Filter className="w-4 h-4 text-gray-400" />
                              <SelectValue placeholder="Category" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            <SelectItem value="uncategorized">No Category</SelectItem>
                            {categories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                                  {cat.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Tag Filter with Search */}
                        <div className="relative" ref={tagSearchRef}>
                          <div 
                            className="flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer hover:border-[#14B8A6] min-w-[180px]"
                            onClick={() => setShowTagDropdown(true)}
                          >
                            <Tag className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-500">
                              {filterTags.length > 0 ? `${filterTags.length} tags` : 'Filter by tags'}
                            </span>
                          </div>
                          
                          {showTagDropdown && (
                            <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-20">
                              <div className="p-2 border-b">
                                <Input
                                  placeholder="Search tags..."
                                  value={tagSearchQuery}
                                  onChange={(e) => setTagSearchQuery(e.target.value)}
                                  className="h-8"
                                  autoFocus
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto p-2">
                                {tagSearchResults.length === 0 ? (
                                  <p className="text-sm text-gray-500 text-center py-2">No tags found</p>
                                ) : (
                                  tagSearchResults.map(tag => (
                                    <div
                                      key={tag.id}
                                      onClick={() => toggleFilterTag(tag.name)}
                                      className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100"
                                    >
                                      {tag.color ? (
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                                      ) : (
                                        <Tag className="w-3 h-3 text-gray-400" />
                                      )}
                                      <span className="text-sm">{tag.name}</span>
                                      <span className="text-xs text-gray-400 ml-auto">({tag.usage_count})</span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* View Mode Toggle */}
                        <div className="flex items-center border rounded-lg ml-auto">
                          <Button
                            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('grid')}
                            className="rounded-r-none"
                          >
                            <Grid className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('list')}
                            className="rounded-l-none"
                          >
                            <List className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Upload Button */}
                        <Button
                          onClick={() => setUploadDialogOpen(true)}
                          className="bg-[#14B8A6] hover:bg-[#0D9488]"
                          data-testid="upload-images-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </Button>
                      </div>

                      {/* Row 2: Active Filters & Bulk Actions */}
                      {(filterCategory !== 'all' || filterTags.length > 0 || selectedImages.size > 0) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Active category filter */}
                          {filterCategory !== 'all' && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              {filterCategory === 'uncategorized' ? 'No Category' : categories.find(c => c.id === filterCategory)?.name}
                              <X 
                                className="w-3 h-3 cursor-pointer" 
                                onClick={() => setFilterCategory('all')}
                              />
                            </Badge>
                          )}
                          
                          {/* Active tag filters */}
                          {filterTags.map(tag => (
                            <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                              {tag}
                              <X 
                                className="w-3 h-3 cursor-pointer" 
                                onClick={() => toggleFilterTag(tag)}
                              />
                            </Badge>
                          ))}

                          {/* Clear all filters */}
                          {(filterCategory !== 'all' || filterTags.length > 0) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={clearAllFilters}
                            >
                              Clear all
                            </Button>
                          )}

                          {/* Bulk Actions */}
                          {selectedImages.size > 0 && (
                            <div className="flex items-center gap-2 ml-auto border-l pl-4">
                              <span className="text-sm text-gray-500">
                                {selectedImages.size} selected
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openMoveDialog(Array.from(selectedImages))}
                              >
                                <Move className="w-4 h-4 mr-1" />
                                Move
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedImages(new Set())}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent>
                    {imagesLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-[#14B8A6]" />
                      </div>
                    ) : images.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                        <ImagePlus className="w-16 h-16 mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No images found</p>
                        <p className="text-sm">
                          {searchQuery || filterCategory !== 'all' || filterTags.length > 0 
                            ? 'Try different filters' 
                            : 'Upload images to get started'}
                        </p>
                      </div>
                    ) : viewMode === 'grid' ? (
                      /* Grid View */
                      <div className="grid grid-cols-4 gap-4">
                        {images.map(image => (
                          <div
                            key={image.id}
                            className={`relative group rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                              selectedImages.has(image.id) 
                                ? 'border-[#14B8A6] ring-2 ring-[#14B8A6]/20' 
                                : 'border-gray-200 hover:border-[#14B8A6]'
                            }`}
                            onClick={() => openImagePreview(image)}
                            data-testid={`image-${image.id}`}
                          >
                              {/* Selection Checkbox */}
                              <div
                                className={`absolute top-2 left-2 z-10 w-6 h-6 rounded flex items-center justify-center cursor-pointer transition-opacity ${
                                  selectedImages.has(image.id)
                                    ? 'bg-[#14B8A6] text-white opacity-100'
                                    : 'bg-white/80 opacity-0 group-hover:opacity-100'
                                }`}
                                onClick={(e) => { e.stopPropagation(); toggleImageSelection(image.id); }}
                              >
                                {selectedImages.has(image.id) ? (
                                  <Check className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 border-2 border-gray-400 rounded" />
                                )}
                              </div>

                              {/* Category Badge */}
                              {image.category && (
                                <div className="absolute top-2 right-2 z-10">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: image.category.color }} title={image.category.name} />
                                </div>
                              )}

                              {/* Thumbnail */}
                              <div className="aspect-square bg-gray-100">
                                <img src={`${BACKEND_URL}${image.thumbnail_url || image.url}`} alt={image.original_filename} className="w-full h-full object-cover" loading="lazy" />
                              </div>

                              {/* Info Overlay */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-white text-xs truncate">{image.original_filename}</p>
                                {image.tags?.length > 0 && (
                                  <div className="flex gap-1 mt-1 flex-wrap">
                                    {image.tags.slice(0, 2).map(tag => (
                                      <span key={tag} className="text-white/80 text-xs bg-white/20 px-1 rounded">{tag}</span>
                                    ))}
                                    {image.tags.length > 2 && <span className="text-white/60 text-xs">+{image.tags.length - 2}</span>}
                                  </div>
                                )}
                              </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* List View */
                      <div className="space-y-2">
                        {images.map(image => (
                          <div
                            key={image.id}
                            className={`flex items-center gap-4 p-2 rounded-lg border cursor-pointer transition-all ${
                              selectedImages.has(image.id)
                                ? 'border-[#14B8A6] bg-[#14B8A6]/5'
                                : 'border-gray-200 hover:border-[#14B8A6]'
                            }`}
                            onClick={() => openImagePreview(image)}
                          >
                            {/* Checkbox */}
                            <div
                              className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer flex-shrink-0 ${
                                selectedImages.has(image.id)
                                  ? 'bg-[#14B8A6] text-white'
                                  : 'border-2 border-gray-300'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleImageSelection(image.id);
                              }}
                            >
                              {selectedImages.has(image.id) && <Check className="w-3 h-3" />}
                            </div>

                            {/* Thumbnail */}
                            <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-100">
                              <img
                                src={`${BACKEND_URL}${image.thumbnail_url || image.url}`}
                                alt={image.original_filename}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{image.original_filename}</p>
                              <p className="text-xs text-gray-500">
                                {image.width}x{image.height} • {formatSize(image.size_bytes)} • {image.format.toUpperCase()}
                              </p>
                            </div>

                            {/* Category */}
                            {image.category && (
                              <Badge style={{ backgroundColor: image.category.color }} className="text-white text-xs">
                                {image.category.name}
                              </Badge>
                            )}

                            {/* Tags */}
                            {image.tags?.length > 0 && (
                              <div className="flex items-center gap-1">
                                {image.tags.slice(0, 3).map(tag => (
                                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                                ))}
                                {image.tags.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{image.tags.length - 3}</Badge>
                                )}
                              </div>
                            )}

                            {/* Quick Actions */}
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMoveDialog([image.id]);
                                }}
                              >
                                <Move className="w-4 h-4" />
                              </Button>
                              {isFullAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setItemToDelete({ type: 'image', item: image });
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pagination */}
                    {pagination.total > 0 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">
                            Showing {getPaginationInfo().start}-{getPaginationInfo().end} of {getPaginationInfo().total} images
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">Per page:</span>
                            <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
                              <SelectTrigger className="w-20 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAGE_SIZE_OPTIONS.map(size => (
                                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {pagination.total_pages > 1 && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={pagination.page === 1}
                              onClick={() => fetchImages(currentFolder?.id || null, pagination.page - 1, searchQuery, filterCategory, filterTags, pageSize)}
                            >
                              Previous
                            </Button>
                            <span className="text-sm text-gray-500">
                              Page {pagination.page} of {pagination.total_pages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={pagination.page === pagination.total_pages}
                              onClick={() => fetchImages(currentFolder?.id || null, pagination.page + 1, searchQuery, filterCategory, filterTags, pageSize)}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          {isFullAdmin && (
            <TabsContent value="settings">
              <div className="grid grid-cols-2 gap-6">
                {/* Categories */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Palette className="w-5 h-5" />
                          Categories
                        </CardTitle>
                        <CardDescription>Organize images by type</CardDescription>
                      </div>
                      <Button onClick={() => openCategoryDialog()} size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {categories.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No categories yet</p>
                    ) : (
                      <div className="space-y-2">
                        {categories.map(cat => (
                          <div key={cat.id} className="flex items-center gap-3 p-3 border rounded-lg">
                            <div 
                              className="w-4 h-4 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: cat.color }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium">{cat.name}</p>
                              {cat.description && (
                                <p className="text-xs text-gray-500 truncate">{cat.description}</p>
                              )}
                            </div>
                            <Badge variant="outline">{cat.image_count} images</Badge>
                            <Button variant="ghost" size="sm" onClick={() => openCategoryDialog(cat)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => handleDeleteCategory(cat)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Tags */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Tag className="w-5 h-5" />
                          Predefined Tags
                        </CardTitle>
                        <CardDescription>Common tags for quick selection</CardDescription>
                      </div>
                      <Button onClick={() => openTagDialog()} size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {tags.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No tags yet</p>
                    ) : (
                      <div className="space-y-2">
                        {tags.map(tag => (
                          <div key={tag.id} className="flex items-center gap-3 p-3 border rounded-lg">
                            {tag.color ? (
                              <div 
                                className="w-4 h-4 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: tag.color }}
                              />
                            ) : (
                              <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium">{tag.name}</p>
                            </div>
                            <Badge variant="outline">{tag.usage_count} uses</Badge>
                            <Button variant="ghost" size="sm" onClick={() => openTagDialog(tag)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => handleDeleteTag(tag)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Best Practices Tab */}
          <TabsContent value="best-practices">
            <div className="max-w-3xl space-y-8 py-2">
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">Image Best Practices</h2>
                <p className="text-sm text-gray-500">Follow these guidelines before uploading images to keep the platform fast and professional.</p>
              </div>

              {/* Compression */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-md bg-blue-100"><Zap className="w-4 h-4 text-blue-600" /></div>
                  <h3 className="font-medium text-gray-900">Step 1 — Compress before uploading</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">Large images slow down the platform for all users. Aim for under 300 KB per image. Use one of these free tools:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { name: 'TinyJPG / TinyPNG', desc: 'Best for JPEG & PNG. Drag-and-drop, batch up to 20 files free.', url: 'https://tinyjpg.com' },
                    { name: 'iLoveIMG', desc: 'Compress, resize, and convert. Works on mobile too.', url: 'https://www.iloveimg.com/compress-image' },
                    { name: 'Compressor.io', desc: 'Lossy & lossless modes. Great for WebP output.', url: 'https://compressor.io' },
                  ].map(tool => (
                    <a key={tool.name} href={tool.url} target="_blank" rel="noopener noreferrer"
                      className="flex flex-col justify-between rounded-lg border border-blue-100 bg-blue-50 p-4 hover:border-blue-300 hover:bg-blue-100 transition-colors group"
                      data-testid={`compression-tool-${tool.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-blue-900">{tool.name}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-blue-400 group-hover:text-blue-600" />
                        </div>
                        <p className="text-xs text-blue-700">{tool.desc}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Watermarking */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-md bg-violet-100"><Droplets className="w-4 h-4 text-violet-600" /></div>
                  <h3 className="font-medium text-gray-900">Step 2 — Watermark original creations</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">If the image is original content created by HelmTorq, add a subtle watermark before uploading to protect ownership.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { name: 'Watermarkly', desc: 'Batch watermarking with logo or text. Supports drag-and-drop folders.', url: 'https://watermarkly.com' },
                    { name: 'WatermarkImage.com', desc: 'Quick free online watermark. No sign-up needed.', url: 'https://watermarkimage.com' },
                  ].map(tool => (
                    <a key={tool.name} href={tool.url} target="_blank" rel="noopener noreferrer"
                      className="flex flex-col justify-between rounded-lg border border-violet-100 bg-violet-50 p-4 hover:border-violet-300 hover:bg-violet-100 transition-colors group"
                      data-testid={`watermark-tool-${tool.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-violet-900">{tool.name}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-violet-400 group-hover:text-violet-600" />
                        </div>
                        <p className="text-xs text-violet-700">{tool.desc}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Caption tip */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-medium text-amber-900 mb-1">Add a caption to every image</p>
                <p className="text-sm text-amber-800">Set a caption during upload, or open any image in the Library to add or edit it. Captions pre-fill automatically wherever the image is used — in questions, notes, and answers.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Folder Dialog */}
        <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                data-testid="new-folder-name"
              />
              {currentFolder && currentFolder !== 'uncategorized' && (
                <p className="text-sm text-gray-500">
                  Will be created inside: {currentFolder.name}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateFolder} 
                disabled={saving}
                className="bg-[#14B8A6] hover:bg-[#0D9488]"
                data-testid="create-folder-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Folder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rename Folder Dialog */}
        <Dialog open={renameFolderOpen} onOpenChange={setRenameFolderOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rename Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                placeholder="Folder name"
                value={newFolderRename}
                onChange={(e) => setNewFolderRename(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameFolderOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRenameFolder} 
                disabled={saving}
                className="bg-[#14B8A6] hover:bg-[#0D9488]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Rename
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog — shared component */}
        <PictureManagerUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          folderId={currentFolder?.id || null}
          onImageAdded={() => {
            fetchImages(currentFolder?.id || null, pagination.page, searchQuery, filterCategory, filterTags, pageSize);
            fetchData();
          }}
        />

        {/* Image Preview Dialog with Inline Editing */}
        <Dialog open={imagePreviewOpen} onOpenChange={(open) => {
          if (!open && hasUnsavedChanges()) {
            if (!window.confirm('You have unsaved changes. Close anyway?')) return;
          }
          setImagePreviewOpen(open);
        }}>
          <DialogContent className="max-w-4xl">
            {selectedImage && (
              <>
                <DialogHeader>
                  <DialogTitle className="truncate">{selectedImage.original_filename}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-6 py-4">
                  {/* Image Preview */}
                  <div className="col-span-2 bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={`${BACKEND_URL}${selectedImage.url}`}
                      alt={selectedImage.original_filename}
                      className="w-full h-auto max-h-[60vh] object-contain"
                    />
                  </div>

                  {/* Image Details & Editing */}
                  <div className="space-y-4 overflow-y-auto max-h-[60vh]">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Dimensions</p>
                        <p className="text-sm">{selectedImage.width} × {selectedImage.height}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Size</p>
                        <p className="text-sm">{formatSize(selectedImage.size_bytes)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Format</p>
                        <p className="text-sm">{selectedImage.format.toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Uploaded by</p>
                        <p className="text-sm truncate">{selectedImage.uploaded_by_name || 'Unknown'}</p>
                      </div>
                    </div>

                    {/* URL */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">URL</p>
                      <Input
                        value={`${BACKEND_URL}${selectedImage.url}`}
                        readOnly
                        className="text-xs"
                        onClick={(e) => {
                          e.target.select();
                          navigator.clipboard.writeText(`${BACKEND_URL}${selectedImage.url}`);
                          toast.success('URL copied to clipboard');
                        }}
                      />
                    </div>

                    {/* Display Name (Rename) */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Display Name</p>
                      <Input
                        value={previewDisplayName}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPreviewDisplayName(val);
                          const dupe = images.find(img =>
                            img.id !== selectedImage.id &&
                            img.original_filename === val.trim() &&
                            img.folder_id === selectedImage.folder_id
                          );
                          setDisplayNameWarning(dupe ? `Another image named "${val.trim()}" exists in this folder.` : '');
                        }}
                        className="text-sm"
                        placeholder="Image display name"
                        data-testid="preview-display-name-input"
                      />
                      {displayNameWarning && (
                        <p className="text-xs text-amber-600 mt-1">{displayNameWarning}</p>
                      )}
                    </div>

                    {/* Caption */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Caption</p>
                      <Input
                        value={previewCaption}
                        onChange={(e) => setPreviewCaption(e.target.value)}
                        className="text-sm"
                        placeholder="Describe what's in this image..."
                        data-testid="preview-caption-input"
                      />
                      <p className="text-xs text-gray-400 mt-1">Pre-fills everywhere this image is used</p>
                    </div>

                    {/* Category - Editable */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Category</p>
                      <Select 
                        value={previewCategory || 'none'} 
                        onValueChange={(val) => setPreviewCategory(val === 'none' ? '' : val)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tags - Editable */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Tags</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {previewTags.map(tag => (
                          <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <X 
                              className="w-3 h-3 cursor-pointer" 
                              onClick={() => removePreviewTag(tag)}
                            />
                          </Badge>
                        ))}
                      </div>
                      <div className="relative">
                        <Input
                          placeholder="Add tag..."
                          value={previewTagInput}
                          onChange={(e) => {
                            setPreviewTagInput(e.target.value);
                            fetchPreviewTagSuggestions(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && previewTagInput.trim()) {
                              e.preventDefault();
                              addPreviewTag(previewTagInput);
                            }
                          }}
                          className="text-sm"
                        />
                        {/* Tag Suggestions */}
                        {previewTagSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                            {previewTagSuggestions.map(suggestion => (
                              <div
                                key={suggestion.name}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                                onClick={() => addPreviewTag(suggestion.name)}
                              >
                                {suggestion.color && (
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: suggestion.color }} />
                                )}
                                <span>{suggestion.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Press Enter to add</p>
                    </div>

                    {/* Save Button - Only show if changes */}
                    {hasUnsavedChanges() && (
                      <Button 
                        onClick={saveImageChanges}
                        disabled={previewSaving}
                        className="w-full bg-[#14B8A6] hover:bg-[#0D9488]"
                      >
                        {previewSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Changes
                      </Button>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setImagePreviewOpen(false);
                          openMoveDialog([selectedImage.id]);
                        }}
                      >
                        <Move className="w-4 h-4 mr-2" />
                        Move to Folder
                      </Button>
                      {isFullAdmin && (
                        <Button
                          variant="outline"
                          className="w-full text-red-500 hover:text-red-700"
                          onClick={() => {
                            setItemToDelete({ type: 'image', item: selectedImage });
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Image
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Move Dialog */}
        <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Move {imagesToMove.length} Image{imagesToMove.length > 1 ? 's' : ''}</DialogTitle>
            </DialogHeader>
            <div className="py-4 max-h-96 overflow-y-auto">
              {/* Uncategorized Option */}
              <div
                onClick={() => setTargetFolderId(null)}
                className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                  targetFolderId === null ? 'bg-[#14B8A6]/10 border border-[#14B8A6]' : ''
                }`}
              >
                <FolderOpen className="w-4 h-4 text-gray-400" />
                <span className="text-sm">Uncategorized</span>
              </div>

              {/* Folder Options */}
              {renderFolderOptions(folderTree.folders)}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleMoveImages} 
                disabled={saving}
                className="bg-[#14B8A6] hover:bg-[#0D9488]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Move
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Diagrams"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-2 mt-1">
                  {COLOR_OPTIONS.map(color => (
                    <div
                      key={color}
                      className={`w-8 h-8 rounded-full cursor-pointer border-2 ${
                        categoryForm.color === color ? 'border-gray-800' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setCategoryForm(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveCategory}
                disabled={saving}
                className="bg-[#14B8A6] hover:bg-[#0D9488]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tag Dialog */}
        <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTag ? 'Edit Tag' : 'New Tag'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={tagForm.name}
                  onChange={(e) => setTagForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., engine"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Will be converted to lowercase</p>
              </div>
              <div>
                <Label>Color (optional)</Label>
                <div className="flex gap-2 mt-1">
                  <div
                    className={`w-8 h-8 rounded-full cursor-pointer border-2 flex items-center justify-center ${
                      !tagForm.color ? 'border-gray-800' : 'border-transparent'
                    } bg-gray-200`}
                    onClick={() => setTagForm(prev => ({ ...prev, color: '' }))}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </div>
                  {COLOR_OPTIONS.map(color => (
                    <div
                      key={color}
                      className={`w-8 h-8 rounded-full cursor-pointer border-2 ${
                        tagForm.color === color ? 'border-gray-800' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setTagForm(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTagDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTag}
                disabled={saving}
                className="bg-[#14B8A6] hover:bg-[#0D9488]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingTag ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Delete {itemToDelete?.type === 'folder' ? 'Folder' : 'Image'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {itemToDelete?.type === 'folder' ? (
                <p>
                  Are you sure you want to delete the folder &quot;{itemToDelete.item.name}&quot;?
                  <br />
                  <span className="text-sm text-gray-500">
                    Images in this folder will be moved to Uncategorized.
                  </span>
                </p>
              ) : (
                <p>
                  Are you sure you want to delete &quot;{itemToDelete?.item?.original_filename}&quot;?
                  <br />
                  <span className="text-sm text-gray-500">
                    This action cannot be undone.
                  </span>
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  if (itemToDelete?.type === 'folder') {
                    handleDeleteFolder(itemToDelete.item);
                  } else {
                    handleDeleteImage(itemToDelete.item);
                  }
                }}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <ExportModal
          open={showExportModal}
          onOpenChange={setShowExportModal}
          title="Export Picture Metadata"
          description="Export image metadata (URLs, tags, categories) to CSV or Excel format"
          filters={[
            {
              key: 'folder_id',
              label: 'Folder',
              value: exportFilters.folder_id,
              onChange: (value) => setExportFilters(prev => ({ ...prev, folder_id: value })),
              options: folderTree.folders?.map(f => ({ value: f.id, label: f.name })) || []
            },
            {
              key: 'category_id',
              label: 'Category',
              value: exportFilters.category_id,
              onChange: (value) => setExportFilters(prev => ({ ...prev, category_id: value })),
              options: categories.map(c => ({ value: c.id, label: c.name }))
            }
          ]}
          columns={[
            { key: 'filename', label: 'Filename' },
            { key: 'url', label: 'URL' },
            { key: 'folder_path', label: 'Folder' },
            { key: 'category_name', label: 'Category' },
            { key: 'tags', label: 'Tags' },
            { key: 'alt_text', label: 'Alt Text' },
            { key: 'file_size', label: 'File Size (bytes)' },
            { key: 'mime_type', label: 'Type' },
            { key: 'width', label: 'Width' },
            { key: 'height', label: 'Height' },
            { key: 'uploaded_by_name', label: 'Uploaded By' },
            { key: 'created_at', label: 'Uploaded At' }
          ]}
          onExport={handleExport}
          exporting={exporting}
          totalCount={exportCount}
        />
      </div>
    </Layout>
  );
};

// Folder Tree Item Component with Rename
const FolderTreeItem = ({ folder, currentFolder, onSelect, onRename, onDelete, depth = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = folder.children?.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-1 p-2 rounded cursor-pointer hover:bg-gray-100 group ${
          currentFolder?.id === folder.id ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : ''
        }`}
        style={{ paddingLeft: `${8 + depth * 12}px` }}
      >
        {hasChildren ? (
          <ChevronRight
            className={`w-4 h-4 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          />
        ) : (
          <div className="w-4" />
        )}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="flex items-center gap-2 flex-1 min-w-0"
                onClick={() => onSelect(folder)}
              >
                <Folder className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                <span className="text-sm truncate">{folder.name}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">({folder.image_count})</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[200px]">
              <p className="text-xs">{folder.name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Rename Button */}
        {onRename && (
          <Button
            variant="ghost"
            size="sm"
            className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onRename(folder);
            }}
          >
            <Edit2 className="w-3 h-3" />
          </Button>
        )}
        {/* Delete Button */}
        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(folder);
            }}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      {hasChildren && expanded && (
        <div>
          {folder.children.map(child => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              currentFolder={currentFolder}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PictureManagerPage;
